import * as _ from 'lodash';
import { useEffect, useRef, useState } from 'react';

import { get } from '../../../redux/api';
import type { AccountLoginStateResponse } from '../../../../typings/api';

type LoginMethods = {
  bitbucket?: boolean;
  github?: boolean;
  gitlab?: boolean;
  google?: boolean;
};

/**
 * Makes a request to /account/login_state once on mount, then manages the
 * state of the active OAuth login method options.
 * The LoginMethods are updated once after the request to /login/next,
 * which is why we export a method to update them.
 *
 * @param {boolean} skip - Whether to skip the entire fetch request and return false for all login methods.
 */
export default function useGetLoginMethods(
  skip: boolean,
): [LoginMethods, (updatedLoginMethods: Partial<LoginMethods>) => void] {
  const [loginMethods, setLoginMethods] = useState<LoginMethods>({
    bitbucket: false,
    github: false,
    gitlab: false,
    google: false,
  });

  const onUpdateLoginMethods = (updatedMethods: Partial<LoginMethods>) => setLoginMethods((loginMethods: LoginMethods) => ({
    ...loginMethods,
    ...updatedMethods,
  }));

  //  `skip` is read once, on mount, so a later change does not refetch.
  const skipOnMount = useRef(skip);

  //  Fetch the login methods once, on mount.
  useEffect(() => {
    const fetchLoginMethods = async () => {
      const { data } = await get<AccountLoginStateResponse>('/account/login_state');
      //  Remove 'subdomain' as that is handled by the parent.
      if (data?.loginMethods) {
        onUpdateLoginMethods(_.omit(data.loginMethods, 'subdomain'));
      }
    };

    if (!skipOnMount.current) {
      fetchLoginMethods();
    }
  }, []);

  return [
    loginMethods,
    onUpdateLoginMethods,
  ];
}
